import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { pmAssignmentsApi, pmTypesApi, equipmentApi } from '@/api';
import { toast } from '@/hooks/use-toast';
import type { PmAssignment, PmType } from '@/types';

const formSchema = z.object({
  equipmentId: z.string().min(1, 'Equipment is required'),
  pmTypeId: z.string().min(1, 'PM Type is required'),
  startDate: z.string().optional(),
  dayOfMonth: z.coerce.number().int().min(1).max(31).optional().or(z.literal('')),
  autoCreateDaysBeforeOverride: z.coerce.number().int().min(0).optional().or(z.literal('')),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  existing?: PmAssignment;
  defaultEquipmentId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function PmAssignmentForm({ existing, defaultEquipmentId, onSuccess, onCancel }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!existing;

  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isEdit
      ? {
          equipmentId: String(existing.equipmentId),
          pmTypeId: String(existing.pmTypeId),
          startDate: existing.startDate ?? '',
          dayOfMonth: existing.dayOfMonth ?? '',
          autoCreateDaysBeforeOverride: existing.autoCreateDaysBeforeOverride ?? '',
        }
      : {
          equipmentId: defaultEquipmentId ? String(defaultEquipmentId) : '',
          pmTypeId: '',
          startDate: new Date().toISOString().slice(0, 10),
          dayOfMonth: '',
          autoCreateDaysBeforeOverride: '',
        },
  });

  const selectedPmTypeId = form.watch('pmTypeId');

  const { data: pmTypesData } = useQuery({
    queryKey: ['pmTypes'],
    queryFn: () => pmTypesApi.list(),
  });

  const { data: equipmentData } = useQuery({
    queryKey: ['equipment', 'active'],
    queryFn: () => equipmentApi.list({ pageSize: 500 }),
    enabled: !defaultEquipmentId || isEdit,
  });

  const pmTypes = (pmTypesData ?? []).filter((t: PmType) => t.isActive && t.triggerType === 'time');

  const selectedPmType = pmTypes.find((t: PmType) => String(t.id) === selectedPmTypeId);
  const isDayOfMonth = selectedPmType?.scheduleMode === 'day_of_month' || selectedPmType?.scheduleMode === 'day_of_week_of_month';

  const filteredEquipment = useMemo(() => {
    const items = equipmentData?.items ?? [];
    if (!equipmentSearch.trim()) return items;
    const q = equipmentSearch.toLowerCase();
    return items.filter((e) =>
      e.unitNumber.toLowerCase().includes(q) ||
      (e.description ?? '').toLowerCase().includes(q) ||
      (e.company ?? '').toLowerCase().includes(q)
    );
  }, [equipmentData, equipmentSearch]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => pmAssignmentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmAssignments'] });
      toast({ title: 'PM assignment created' });
      onSuccess();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to create assignment';
      toast({ title: msg, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => pmAssignmentsApi.update(existing!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmAssignments'] });
      toast({ title: 'PM assignment updated' });
      onSuccess();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to update assignment';
      toast({ title: msg, variant: 'destructive' });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function onSubmit(values: FormData) {
    const payload: Record<string, unknown> = {
      equipmentId: Number(values.equipmentId),
      pmTypeId: Number(values.pmTypeId),
      startDate: values.startDate || undefined,
      dayOfMonth: values.dayOfMonth !== '' && values.dayOfMonth !== undefined ? Number(values.dayOfMonth) : undefined,
      autoCreateDaysBeforeOverride: values.autoCreateDaysBeforeOverride !== '' && values.autoCreateDaysBeforeOverride !== undefined
        ? Number(values.autoCreateDaysBeforeOverride)
        : null,
    };

    if (isEdit) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Equipment */}
        {!defaultEquipmentId && (
          <FormField
            control={form.control}
            name="equipmentId"
            render={({ field }) => {
              const selected = equipmentData?.items.find((e) => String(e.id) === field.value);
              return (
                <FormItem>
                  <FormLabel>Equipment</FormLabel>
                  <div className="relative">
                    <button
                      type="button"
                      disabled={isEdit}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                      onClick={() => !isEdit && setEquipmentDropdownOpen((o) => !o)}
                    >
                      <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
                        {selected
                          ? `${selected.unitNumber}${selected.description ? ` — ${selected.description}` : ''}`
                          : 'Select equipment…'}
                      </span>
                      <svg className="h-4 w-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {equipmentDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
                        <div className="p-2">
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search by unit, description…"
                            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            value={equipmentSearch}
                            onChange={(e) => setEquipmentSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <ul className="max-h-52 overflow-y-auto pb-1">
                          {filteredEquipment.length === 0 && (
                            <li className="px-3 py-2 text-sm text-muted-foreground">No results.</li>
                          )}
                          {filteredEquipment.map((e) => (
                            <li
                              key={e.id}
                              className="cursor-pointer px-3 py-2 text-sm hover:bg-accent"
                              onMouseDown={() => {
                                field.onChange(String(e.id));
                                setEquipmentSearch('');
                                setEquipmentDropdownOpen(false);
                              }}
                            >
                              <span className="font-medium">{e.unitNumber}</span>
                              {e.description && <span className="text-muted-foreground"> — {e.description}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        )}

        {/* PM Type */}
        <FormField
          control={form.control}
          name="pmTypeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>PM Type</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select PM type…" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {pmTypes.map((t: PmType) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      <div className="flex items-center gap-2">
                        {t.color && (
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                        )}
                        {t.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Start Date */}
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Day of Month override (only for day_of_month schedule modes) */}
        {isDayOfMonth && (
          <FormField
            control={form.control}
            name="dayOfMonth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Day of Month Override <span className="text-muted-foreground font-normal">(1–31, optional)</span></FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    placeholder={`Default: ${selectedPmType?.dayOfMonth ?? '—'}`}
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Auto-create days before override */}
        <FormField
          control={form.control}
          name="autoCreateDaysBeforeOverride"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Days Before Due to Auto-Create Repair Order{' '}
                <span className="text-muted-foreground font-normal">
                  (override — default: {selectedPmType?.alertDaysBefore ?? '—'} days)
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  placeholder={selectedPmType?.alertDaysBefore != null ? String(selectedPmType.alertDaysBefore) : 'Use PM type default'}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Assignment'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
